import { useState, useEffect, useRef } from 'react'

interface ImplMultiSelectProps {
  label: string
  options: string[]
  selected: string[]
  onChange: (selected: string[]) => void
  renderOption?: (option: string) => React.ReactNode
}

export function ImplMultiSelect({ label, options, selected, onChange, renderOption }: ImplMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (val: string) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val))
    else onChange([...selected, val])
  }

  const activeCount = selected.length
  const buttonLabel = activeCount === 0 ? label : `${label} (${activeCount})`
  const hasActive = activeCount > 0

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[13px] cursor-pointer border transition-colors ${
          hasActive
            ? 'bg-primary/15 border-primary text-primary'
            : 'bg-[var(--bg-primary)] border-[var(--border-color)] text-[var(--text-primary)]'
        }`}
      >
        {buttonLabel}
        <span className="text-[10px] text-[var(--text-muted)]">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-[100] min-w-[200px] max-h-[260px] overflow-y-auto bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg">
          {activeCount > 0 && (
            <div
              onClick={() => onChange([])}
              className="px-3 py-2 text-[11px] text-primary cursor-pointer border-b border-[var(--border-color)] font-semibold hover:bg-[var(--bg-primary)] transition-colors"
            >
              ✕ Clear all
            </div>
          )}
          {options.map((opt) => {
            const isChecked = selected.includes(opt)
            return (
              <div
                key={opt}
                onClick={() => toggle(opt)}
                className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-[13px] transition-colors ${
                  isChecked ? 'text-[var(--text-primary)] bg-primary/10' : 'text-[var(--text-muted)] hover:bg-[var(--bg-primary)]'
                }`}
              >
                <div
                  className={`w-3.5 h-3.5 rounded-[3px] flex-shrink-0 flex items-center justify-center ${
                    isChecked ? 'bg-primary border-primary' : 'border-[var(--border-color)]'
                  }`}
                  style={{ border: `1.5px solid ${isChecked ? 'var(--color-primary)' : 'var(--border-color)'}` }}
                >
                  {isChecked && <span className="text-white text-[9px] font-bold">✓</span>}
                </div>
                {renderOption ? renderOption(opt) : opt}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
